import { useState, useEffect, useCallback } from 'react';
import type { SupabaseClient, User } from '@supabase/supabase-js';
import { processOAuthUserRole } from '../lib/oauthUserRole';
import { trackError } from '../lib/analytics';

interface AuthError {
  message: string;
  type: 'network' | 'auth' | 'session' | 'unknown';
  retryable: boolean;
}

const getSupabase = (() => {
  let promise: Promise<SupabaseClient> | null = null;
  return () => {
    if (!promise) {
      promise = import('../lib/supabase').then((m) => m.supabase);
    }
    return promise;
  };
})();

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<AuthError | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [lastActivity, setLastActivity] = useState<Date>(new Date());

  const updateActivity = useCallback(() => {
    setLastActivity(new Date());
  }, []);

  const handleAuthError = useCallback((err: unknown, context: string): AuthError => {
    let authError: AuthError;

    const anyErr = err as { message?: string; code?: string };

    if (anyErr?.message?.includes('network') || anyErr?.code === 'NETWORK_ERROR') {
      authError = {
        message: 'Network connection issue. Please check your internet connection.',
        type: 'network',
        retryable: true,
      };
    } else if (anyErr?.message?.includes('Invalid') || anyErr?.code === 'INVALID_CREDENTIALS') {
      authError = {
        message: 'Invalid credentials. Please check your email and password.',
        type: 'auth',
        retryable: false,
      };
    } else if (anyErr?.message?.includes('session') || anyErr?.code === 'SESSION_EXPIRED') {
      authError = {
        message: 'Your session has expired. Please sign in again.',
        type: 'session',
        retryable: false,
      };
    } else {
      authError = {
        message: anyErr?.message || 'An unexpected error occurred. Please try again.',
        type: 'unknown',
        retryable: true,
      };
    }

    trackError(authError.type, authError.message, context);

    return authError;
  }, []);

  const retryAuth = useCallback(async () => {
    if (retryCount >= 3) {
      setError({
        message: 'Unable to connect after multiple attempts. Please try again later.',
        type: 'network',
        retryable: false,
      });
      return;
    }

    setRetryCount((prev) => prev + 1);
    setError(null);

    const delay = Math.pow(2, retryCount) * 1000;
    setTimeout(() => {
      void getInitialSession();
    }, delay);
  }, [retryCount]);

  const getInitialSession = useCallback(async () => {
    const supabase = await getSupabase();
    try {
      setLoading(true);
      setError(null);

      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();

      if (sessionError) {
        throw sessionError;
      }

      setUser(session?.user ?? null);
      setRetryCount(0);

      if (session?.user) {
        try {
          const { error: profileError } = await supabase
            .from('user_profiles')
            .select('user_id')
            .eq('user_id', session.user.id)
            .limit(1);

          if (profileError && profileError.code === 'UNAUTHORIZED') {
            await supabase.auth.signOut();
            setUser(null);
          }
        } catch (profileError) {
          console.warn('Could not validate session:', profileError);
        }
      }
    } catch (err) {
      const authError = handleAuthError(err, 'initial_session');
      setError(authError);

      if (authError.retryable && retryCount < 3) {
        void retryAuth();
        return;
      }
    } finally {
      setLoading(false);
    }
  }, [handleAuthError, retryAuth, retryCount]);

  useEffect(() => {
    let cancelled = false;
    let subscription: { unsubscribe: () => void } | null = null;

    void (async () => {
      const supabase = await getSupabase();
      if (cancelled) return;

      await getInitialSession();
      if (cancelled) return;

      const {
        data: { subscription: sub },
      } = supabase.auth.onAuthStateChange(async (event, session) => {
        try {
          const nextUser = session?.user ?? null;
          setUser(nextUser);
          setLoading(false);
          setError(null);

          updateActivity();

          if (event === 'SIGNED_IN' && nextUser) {
            try {
              await processOAuthUserRole(nextUser);
            } catch (roleError) {
              console.warn('Error processing OAuth user role:', roleError);
            }
          }

          if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
            setRetryCount(0);
          }
        } catch (err) {
          const authError = handleAuthError(err, `auth_state_change_${event}`);
          setError(authError);
        }
      });
      subscription = sub;
    })();

    return () => {
      cancelled = true;
      subscription?.unsubscribe();
    };
  }, [getInitialSession, handleAuthError, updateActivity]);

  const signOut = useCallback(async () => {
    const supabase = await getSupabase();
    try {
      setLoading(true);
      setError(null);

      const { error: signOutError } = await supabase.auth.signOut();
      if (signOutError) throw signOutError;

      setUser(null);
      setRetryCount(0);
    } catch (err) {
      const authError = handleAuthError(err, 'sign_out');
      setError(authError);
      console.error('Error signing out:', err);
    } finally {
      setLoading(false);
    }
  }, [handleAuthError]);

  const isSessionExpired = useCallback(() => {
    if (!user || !lastActivity) return false;

    const hoursSinceActivity = (Date.now() - lastActivity.getTime()) / (1000 * 60 * 60);
    return hoursSinceActivity > 24;
  }, [user, lastActivity]);

  const refreshSession = useCallback(async () => {
    const supabase = await getSupabase();
    try {
      const { data, error: refreshError } = await supabase.auth.refreshSession();
      if (refreshError) throw refreshError;

      setUser(data.session?.user ?? null);
      updateActivity();

      return { success: true, error: null };
    } catch (err) {
      const authError = handleAuthError(err, 'refresh_session');
      setError(authError);
      return { success: false, error: authError };
    }
  }, [handleAuthError, updateActivity]);

  const clearError = useCallback(() => {
    setError(null);
    setRetryCount(0);
  }, []);

  return {
    user,
    loading,
    error,
    isAuthenticated: !!user && !isSessionExpired(),
    isSessionExpired: isSessionExpired(),
    lastActivity,
    signOut,
    refreshSession,
    retryAuth,
    clearError,
    updateActivity,
  };
}
